import React, { useState } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { KpiCard } from '../components/KpiCard'
import { ChartCard } from '../components/ChartCard'
import { AnalyticsTable, RedValueRenderer } from '../components/AnalyticsTable'
import { SubTabBar } from '../components/SubTabBar'
import { LockedStateCard } from '../components/LockedStateCard'
import {
  KpiRow,
  HorizontalBarWrapper,
  HorizontalBarRow,
  HorizontalBarLabel,
  HorizontalBarTrack,
  HorizontalBarFill,
  HorizontalBarValue,
  ChartCardWrapper,
  ChartCardTitle,
} from '../HrAnalytics.styled'
import {
  MOCK_PAYROLL_KPIS,
  MOCK_PAYROLL_PERIOD,
  MOCK_PAYROLL_BY_DEPT,
  MOCK_OVERTIME_BY_DEPT,
} from 'features/hrAnalytics/mockData/payroll.mock'

const HAS_PAYROLL_ACCESS = true

const KpiIcons: Record<string, React.ReactNode> = {
  dollar: <svg viewBox='0 0 24 24'><path d='M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z' /></svg>,
  checkCircle: <svg viewBox='0 0 24 24'><path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' /></svg>,
  warning: <svg viewBox='0 0 24 24'><path d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z' /></svg>,
}

const PAYROLL_DEPT_COLUMNS = [
  { key: 'name', label: 'Name', sortable: true, width: '16%' },
  { key: 'headcount', label: 'Headcount', sortable: true, width: '14%' },
  { key: 'totalCost', label: 'Total Cost', sortable: true, width: '16%' },
  { key: 'basic', label: 'Basic', sortable: true, width: '16%' },
  {
    key: 'overtime',
    label: 'Overtime',
    sortable: true,
    width: '16%',
    renderCell: (val: string) => <RedValueRenderer value={val} />,
  },
  { key: 'allowances', label: 'Allowances', sortable: true, width: '16%' },
]

const CHART_COLORS = {
  basicSalary: '#30B6D1',
  overtime: '#00B04C',
  allowances: '#ff9900',
  deductions: '#FF4F46',
}

export const PayrollTab = () => {
  const [tableTab, setTableTab] = useState('department')

  if (!HAS_PAYROLL_ACCESS) {
    return (
      <LockedStateCard message='Payroll analytics requires elevated permissions. Contact your administration.' />
    )
  }

  const maxOvertime = Math.max(...MOCK_OVERTIME_BY_DEPT.map((d) => d.amount))

  return (
    <>
      <KpiRow>
        {MOCK_PAYROLL_KPIS.slice(0, 3).map((kpi, idx) => (
          <KpiCard
            key={idx}
            icon={KpiIcons[kpi.iconKey || '']}
            value={kpi.value}
            label={kpi.label}
            sublabel={kpi.sublabel}
            trend={kpi.trend}
            accentColor={kpi.accentColor}
          />
        ))}
      </KpiRow>
      <KpiRow>
        {MOCK_PAYROLL_KPIS.slice(3, 6).map((kpi, idx) => (
          <KpiCard
            key={idx + 3}
            icon={KpiIcons[kpi.iconKey || '']}
            value={kpi.value}
            label={kpi.label}
            sublabel={kpi.sublabel}
            trend={kpi.trend}
            accentColor={kpi.accentColor}
          />
        ))}
      </KpiRow>

      <ChartCard
        title='Payroll Period'
        legend={[
          { label: 'Basic Salary', color: CHART_COLORS.basicSalary },
          { label: 'Overtime', color: CHART_COLORS.overtime },
          { label: 'Allowances', color: CHART_COLORS.allowances },
          { label: 'Deductions (Separate)', color: CHART_COLORS.deductions },
        ]}
        rightControls={
          <select style={{ background: 'transparent', border: '1px solid #464255', borderRadius: 8, padding: '4px 8px', color: 'inherit', fontSize: '0.75rem' }}>
            <option>Last 4 months</option>
            <option>Last 6 months</option>
            <option>Last 12 months</option>
          </select>
        }
      >
        <ResponsiveContainer width='100%' height={350}>
          <BarChart data={MOCK_PAYROLL_PERIOD}>
            <CartesianGrid strokeDasharray='3 3' stroke='#46425520' />
            <XAxis dataKey='month' tick={{ fontSize: 11, fill: '#B0B0B0' }} tickLine={false} />
            <YAxis
              tick={{ fontSize: 11, fill: '#B0B0B0' }}
              tickLine={false}
              tickFormatter={(v) => `$${v / 1000}K`}
            />
            <Tooltip formatter={(value: number) => `$${(value / 1000).toFixed(0)}K`} />
            <Bar dataKey='basicSalary' stackId='payroll' fill={CHART_COLORS.basicSalary} radius={[0, 0, 0, 0]} />
            <Bar dataKey='overtime' stackId='payroll' fill={CHART_COLORS.overtime} radius={[0, 0, 0, 0]} />
            <Bar dataKey='allowances' stackId='payroll' fill={CHART_COLORS.allowances} radius={[0, 0, 0, 0]} />
            <Bar dataKey='deductions' fill={CHART_COLORS.deductions} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <SubTabBar
        tabs={[
          { key: 'department', label: 'By Department' },
          { key: 'role', label: 'By Role' },
        ]}
        activeTab={tableTab}
        onTabChange={setTableTab}
      />

      <AnalyticsTable columns={PAYROLL_DEPT_COLUMNS} data={MOCK_PAYROLL_BY_DEPT} />

      <ChartCardWrapper>
        <ChartCardTitle>Overtime by Department</ChartCardTitle>
        <HorizontalBarWrapper>
          {MOCK_OVERTIME_BY_DEPT.map((dept) => (
            <HorizontalBarRow key={dept.name}>
              <HorizontalBarLabel>{dept.name}</HorizontalBarLabel>
              <HorizontalBarTrack>
                <HorizontalBarFill percent={(dept.amount / maxOvertime) * 100} />
              </HorizontalBarTrack>
              <HorizontalBarValue>{dept.label}</HorizontalBarValue>
            </HorizontalBarRow>
          ))}
        </HorizontalBarWrapper>
      </ChartCardWrapper>
    </>
  )
}
